import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";

export default function Security() {
  const [status, setStatus] = useState(null);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll");
      setEnroll(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const verifyEnroll = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify-enroll", { code: code.trim() });
      toast.success("2FA enabled. Save your backup codes somewhere safe.");
      setCode("");
      setEnroll(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const disable2fa = async () => {
    if (!window.confirm("Disable 2FA? Your account will only need a password.")) return;
    try {
      await api.post("/auth/2fa/disable");
      toast.success("2FA disabled.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="security-page">
      <Toaster position="top-right" richColors />
      <div className="overline mb-2 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Security</div>
      <h1 className="text-3xl font-bold text-neutral-900">Two-factor authentication</h1>
      <p className="text-neutral-600 mt-2 max-w-2xl text-sm mb-8">
        Protect your admin login with a TOTP code from Google Authenticator, Authy, or 1Password.
      </p>

      {status?.enabled ? (
        <div className="border border-emerald-300 bg-emerald-50 p-6" data-testid="security-2fa-enabled">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-700 mt-1" />
            <div className="flex-1">
              <div className="font-display text-xl font-bold text-emerald-900">2FA is enabled ✓</div>
              <div className="text-sm text-emerald-800 mt-1">
                Enrolled {status.enrolled_at?.slice(0, 10)} · {status.backup_codes_remaining} backup codes remaining
              </div>
            </div>
            <button onClick={disable2fa} data-testid="security-disable-btn" className="lf-btn-outline text-xs px-3 py-2">
              Disable 2FA
            </button>
          </div>
        </div>
      ) : enroll ? (
        <div className="border border-neutral-200 bg-white p-6" data-testid="security-enroll-panel">
          <div className="overline mb-4">Step 1 · Scan the QR</div>
          <div className="flex gap-6 flex-wrap">
            <img src={enroll.qr_data_url} alt="2FA QR" className="w-52 h-52 border border-neutral-300" data-testid="security-qr-img" />
            <div className="flex-1 min-w-[220px]">
              <div className="text-sm text-neutral-600 mb-3">
                Scan with Google Authenticator, Authy, or 1Password.
                Can&apos;t scan? Enter this secret manually:
              </div>
              <code className="block bg-neutral-900 text-white p-3 font-mono text-xs break-all mb-4">{enroll.secret}</code>
              <div className="border border-amber-300 bg-amber-50 p-3 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <div>
                    <strong className="text-amber-900">Save these backup codes now — shown only once:</strong>
                    <div className="grid grid-cols-2 gap-1 mt-2 font-mono text-xs text-amber-900">
                      {enroll.backup_codes.map((c) => <div key={c} className="bg-white border border-amber-200 px-2 py-1">{c}</div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <form onSubmit={verifyEnroll} className="mt-6 border-t border-neutral-100 pt-6">
            <div className="overline mb-2">Step 2 · Enter current 6-digit code</div>
            <div className="flex gap-2">
              <input
                autoFocus
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="security-verify-code"
                className="border border-neutral-300 px-3 py-3 font-mono text-xl tracking-widest w-40 text-center"
                placeholder="000000"
                required
              />
              <button type="submit" disabled={busy || code.length !== 6} data-testid="security-verify-submit" className="lf-btn-dark px-4 py-3 disabled:opacity-50 flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> {busy ? "Verifying…" : "Verify & enable"}
              </button>
              <button type="button" onClick={() => setEnroll(null)} className="text-neutral-500 text-sm px-3">Cancel</button>
            </div>
          </form>
        </div>
      ) : (
        <div className="border border-neutral-200 bg-white p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-neutral-400 mt-1" />
            <div className="flex-1">
              <div className="font-display text-xl font-bold">2FA not set up</div>
              <div className="text-sm text-neutral-600 mt-1 mb-4">
                Add a second layer of protection to your login. Takes 60 seconds.
              </div>
              <button onClick={startEnroll} disabled={busy} data-testid="security-enroll-btn" className="lf-btn-dark px-4 py-2 text-sm">
                {busy ? "Loading…" : "Enable 2FA"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
